import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Marker, GeoJSONSource } from 'mapbox-gl';

import { markerFactory } from 'utils/';
import { ITrackingConfig, ITrackingData } from 'interfaces';
import { useMediaBreakpointUp, useMapbox } from 'hooks';
import direction, { IDirection } from 'api/direction';
import tracking from 'api/tracking';
import './MapWithTracking.css';
import dayjs from 'dayjs';
import Loader from '../loader/Loader';

interface Props {
    trackingData: ITrackingData | undefined;
    trackingConfig: ITrackingConfig;
    updateEstimatedTime(newDuration?: number): void;
    isTestMode: boolean;
}

const isOneHourBefore = (targetTime: string) => {
    const dateTarget = dayjs(new Date(targetTime));
    return dateTarget.diff(new Date().toISOString(), 'minute') <= 60;
};
const MapWithTracking = ({ trackingData, trackingConfig, updateEstimatedTime, isTestMode }: Props) => {
    const container = useRef<HTMLDivElement | null>(null);
    const toMarker = useRef<Marker | null>(null);
    const fromMarker = useRef<Marker | null>(null);
    const courierMarker = useRef<Marker | null>(null);
    const isDirectionLayerLoaded = useRef<boolean>(false);
    const isDataLoaded = useRef<boolean>(false);
    const queryParameters = new URLSearchParams(window.location.search);
    const lite = queryParameters.get('lite');

    const mediaBreakpointUp = useMediaBreakpointUp();
    const padding = !lite // eslint-disable-line
        ? mediaBreakpointUp('xl')
            ? { top: 100, bottom: 50, left: 750, right: 150 }
            : { top: 80, bottom: 270, left: 50, right: 50 }
        : { top: 35, bottom: 15, left: 80, right: 80 };

    const { map, mapAPI } = useMapbox({
        container,
        padding,
        isTestMode,
    });
    const [status, setStatus] = useState<'pending' | 'ready'>('pending');

    const useCourierPosition: boolean = useMemo(() => {
        const lastEventName = trackingData?.eventList?.sort((a, b) => Date.parse(b.date) - Date.parse(a.date))[0]?.name;
        const dropoffDate = trackingData?.endAt;
        return (
            trackingConfig.enableCourierPosition &&
            lastEventName === 'pickedUp' &&
            isOneHourBefore(dropoffDate!) &&
            !trackingData?.dropoffEffectiveDate
        );
    }, [trackingConfig, trackingData]);

    const getDirection = async ({ from, to }: { from: Marker; to: Marker }): Promise<IDirection | undefined> => {
        const routeData = await direction({
            from: from.getLngLat().toArray(),
            to: to.getLngLat().toArray(),
        });

        return routeData;
    };

    const updateDirection = async ({ from, to }: { from: Marker; to: Marker }): Promise<void> => {
        const { duration, route } = (await getDirection({ from, to })) || {};
        if (route && isDirectionLayerLoaded.current) {
            const source = map.current?.getSource('route') as GeoJSONSource;
            source.setData(route);
        } else if (route) {
            mapAPI.addDirectionLayer(route);
            isDirectionLayerLoaded.current = true;
        }

        updateEstimatedTime(duration);
    };

    const addCourierPosition = async (): Promise<Marker | null> => {
        let marker = null;
        const { connectionToken, deliveryId: shipmentId } = trackingData || {};
        if (connectionToken && shipmentId) {
            const { coordinates } = await tracking.location({ connectionToken, shipmentId });
            if (coordinates && toMarker.current) {
                marker = markerFactory({ kind: 'truck', coordinates });
                if (marker) {
                    mapAPI.addMarker(marker);
                    updateDirection({ from: marker, to: toMarker.current });
                    courierMarker.current = marker;
                }
            }

            return marker;
        }

        return null;
    };

    useEffect(() => {
        // eslint-disable-next-line no-console
        if (status === 'ready') setStatus('pending');
        if (typeof window === 'undefined' || !container.current) return;
        if (!map.current) mapAPI.initMap();
        if (!trackingData || trackingData.status === 'onGoing') {
            mapAPI.addDirectionLayer();
            isDirectionLayerLoaded.current = true;
        }

        if (trackingData && !isDataLoaded.current) {
            const boundsMarker: Marker[] = [];
            const { fromAddress, toAddress } = trackingData;

            toMarker.current = markerFactory({ kind: 'to', coordinates: toAddress.coordinates });
            fromMarker.current = markerFactory({ kind: 'from', coordinates: fromAddress.coordinates });
            if (fromMarker.current) boundsMarker.push(fromMarker.current);
            if (toMarker.current) boundsMarker.push(toMarker.current);

            mapAPI.addMarker(toMarker.current);
            mapAPI.addMarker(fromMarker.current);
            if (boundsMarker.length) {
                const bounds = mapAPI.getBounds(boundsMarker);
                mapAPI.setBounds(bounds);
                mapAPI.addPositionBtn(bounds);
            }
            isDataLoaded.current = true;
        }

        if (useCourierPosition) {
            addCourierPosition().then((marker) => {
                if (marker && toMarker?.current && fromMarker?.current) {
                    const bounds = mapAPI.getBounds([fromMarker.current, toMarker.current, marker]);
                    mapAPI.setBounds(bounds);
                }
            });
        }

        if (trackingData?.status === 'delivered' && courierMarker.current) {
            courierMarker.current.remove();
            mapAPI.removeDirection();
        }

        setStatus('ready');
    }, [trackingData, useCourierPosition]);

    return (
        <>
            {status === 'pending' ? <Loader overlay /> : null}
            <div ref={container} className="map-container" />
        </>
    );
};

export default MapWithTracking;
